/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of selection written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectIsDrillDown,
  selectIsFocusOnSet,
  selectIsRegionDetailed,
  selectRegionIsDisplay,
  selectRegionMapBookingDate,
  selectRegionMapChartBreakdown,
  selectRegionMapFilterList,
  selectRegionMapFocusOn,
  selectRegionTabularIsDisplay,
  selectRegionalBreakdownTabularView,
  selectRegionalData,
  selectRegionalFullData,
  selectRegionalMapDataLoading,
  selectRegionalMapFullDataLoading,
  selectRegionalMapLevel,
  selectRegionalMapSelection,
} from 'modules/dashboard/components/tab-container/regional-map-tab/selectors';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import regionStyles from 'modules/dashboard/components/tab-container/regional-map-tab/regional-map.module.scss';
import { API, EXPORT_OPTIONS } from 'modules/dashboard/constants';
import RegionalMapFilter from 'modules/dashboard/components/tab-container/regional-map-tab/components/filter-panel';
import { regionalMapActions } from 'modules/dashboard/components/tab-container/regional-map-tab/slice';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import {
  selectHotelName,
  selectLocalCurrency,
  selectPmsSync,
  selectTabList,
  selectTargetDate,
  selectVisualFilters,
} from 'modules/dashboard/selectors';
import useAbortableRequest from 'modules/common/hooks/use-abortable-request';
import { isRequestCanceled } from 'modules/common/utils/request';
import MuiTabular from 'modules/common/components/mui-tabular';
import { Box } from '@mui/system';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import CollapseIcon from 'modules/common/components/collapse-icon';
import { useDebouncedCallback } from 'use-debounce';
import GetAppIcon from '@mui/icons-material/GetApp';
import { ENVIRONMENT } from 'config';
import useRegionMap from '../../hooks/use-region-map';
import VisualCard from '../../../visual-card';
import { isEmpty } from '../../../../../../common/helpers/object';
import VisualGrid from '../../../visual-grid';
import { AssistantIcon, CustomToggleSwitch } from '../../../../../../common/components';
import {
  buildBreakdownFilters,
  buildColumnsFromData,
  buildExportColumnsFromData,
  buildHeaderFilterData,
  buildRegionalFilters,
  ensureRegionalMapFullDataFilters,
  getBreakdownValue,
  getHierarchyNeighbors,
  getLevel,
  getRegionalMapDataLastLevel,
  getRelativeLevels,
  hasAnyLevelSelected,
  removeMaxLevel,
  removeSetValueBySuffix,
} from '../../function';
import { getBreakdownName } from '../../../trend-tabular-tabs/function';
import { BREAKDOWN_LIST_GETTER_COLUMNS } from '../../../trend-tabular-tabs/constants';
import {
  ACTION_BUTTON_STYLE,
  AGGREGATE_ROW_STYLE,
  DRILL_DOWN_BUTTON_STYLE,
  TABLE_CONTAINER_STYLE,
} from '../../styles';
import { EXPORT_PDF_BUTTON_STYLE } from '../../../workbook-report/style';
import { REGIONAL_MAP_FULL_DATA_COLUMN } from '../../constant';

/**
 * Regional map report with filters
 * @param {Number} value - Regional map tab value
 * @param {String} accessToken - Power BI access token
 * @param {Object} visuals - Regional map tab visual details
 * @param {Boolean} active - whether Regional map tab is active
 * @param {Boolean} toggleDisabled - PMS sync toggle disabled state
 * @param {Date} latestDate - Latest booking date of selected hotel
 * @param {String} hotelTimezone - Timezone of selected hotel
 * @param {String} reportType - Report type of active tab
 * @returns
 */

const RegionalMap = ({
  value,
  accessToken,
  visuals,
  active,
  latestDate,
  reportType,
  hotelTimezone,
  toggleDisabled,
}) => {
  useRegionMap();
  const dispatch = useDispatch();
  const abortableRequest = useAbortableRequest();
  const IsFocusOnSet = useSelector(selectIsFocusOnSet);
  const regionIsDisplay = useSelector(selectRegionIsDisplay);
  const regionTabularIsDisplay = useSelector(selectRegionTabularIsDisplay);
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  const [isExportProcessing, setIsExportProcessing] = useState(false);
  //
  const bookingDate = useSelector(selectRegionMapBookingDate);
  const currencyType = useSelector(selectLocalCurrency);
  const hotels = useSelector(selectHotelName);
  const targetDate = useSelector(selectTargetDate);
  const chartBreakdown = useSelector(selectRegionMapChartBreakdown);
  const pms = useSelector(selectPmsSync);
  const isDetailed = useSelector(selectIsRegionDetailed);
  const visualFilters = useSelector(selectVisualFilters);
  const organizationId = useSelector(selectOrganizationId);
  const focusOn = useSelector(selectRegionMapFocusOn);
  const regionalMapLevel = useSelector(selectRegionalMapLevel);
  const regionalMapSelection = useSelector(selectRegionalMapSelection);
  const regionalMapData = useSelector(selectRegionalData);
  const isRegionalMapDataLoading = useSelector(selectRegionalMapDataLoading);
  const regionalMapFullData = useSelector(selectRegionalFullData);
  const isRegionalMapFullDataLoading = useSelector(selectRegionalMapFullDataLoading);
  const isDrillDown = useSelector(selectIsDrillDown);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const tableRef = useRef();
  const exportTableRef = useRef();
  //
  const [isRegionLoaded, setIsRegionLoaded] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  //
  const filterList = useSelector(selectRegionMapFilterList);
  const regionalBreakdownTabularView = useSelector(selectRegionalBreakdownTabularView);
  const [isV1Processing, setIsV1Processing] = useState(false);
  const [isRegionalMapVisualRendered, setIsRegionalMapVisualRendered] = useState(false);
  const [regionalMapReport, setRegionalMapReport] = useState({});
  // To get visuals based on the configuration
  const getWidget = (
    index,
    cssClassName,
    setPBIReport = () => {},
    setIsRendered = () => {},
    displayFunction = () => {},
    setIsLoaded = () => {}
  ) => {
    const found = visuals?.find((visual) => visual?.order === index);
    //
    return (
      found && (
        <VisualCard
          cssClassName={cssClassName}
          accessToken={accessToken}
          embedUrl={found?.embedUrl}
          id={found?.reportId}
          visualName={found?.name}
          pageName={found?.pageName}
          pageDisplayName={found?.pageDisplayName}
          reportName={found?.reportName}
          filters={filterList}
          setPBIReport={(report) => setPBIReport(report)}
          setIsLoaded={(d) => setIsLoaded(d)}
          setIsRendered={(d) => setIsRendered(d)}
          tab={TAB_VALUE.REGION_MAP}
          dispatch={dispatch}
          displayFunction={displayFunction}
        />
      )
    );
  };
  // To get visual names based on visual order
  const getVisualName = (index) => {
    const found = visuals?.find((visual) => visual?.order === index);
    return found?.name;
  };
  // Triggered when tab value or active tab change
  useEffect(() => {
    // Scroll up when the regional map tab is clicked
    if (value === TAB_VALUE.REGION_MAP) window.scrollTo(0, 0);
  }, [value, active]);

  const fetchData = useDebouncedCallback(async () => {
    const filters = buildRegionalFilters({
      bookingDate,
      targetStartDate: targetDate?.startDate,
      targetEndDate: targetDate?.endDate,
      currencyType,
      hotels,
      pms,
      focusOn,
      visualFilters,
      chartBreakdown,
      regionalMapLevel,
      regionalMapSelection,
      isDetailed,
    });
    const breakdownFilters = buildBreakdownFilters({
      bookingDate,
      targetStartDate: targetDate?.startDate,
      targetEndDate: targetDate?.endDate,
      currencyType,
      hotels,
      pms,
      visualFilters,
      chartBreakdown,
      isDetailed,
      focusOn,
    });
    (async () => {
      try {
        // fetch breakdown list
        const breakdownListData = await abortableRequest(API.GET_JS_VISUAL_DATA, {
          organizationId,
          columns: BREAKDOWN_LIST_GETTER_COLUMNS,
          filters: breakdownFilters,
        });
        const breakdown = getBreakdownName(isDetailed, visualFilters, chartBreakdown);
        const breakdownList = breakdownListData?.map((item) => item?.[breakdown]);
        if (filters?.breakDownList?.conditions) {
          const isTotalInclude = filters.breakDownList.conditions.values
            .map((v) => v.replace(/\u200B/g, '').trim())
            .includes('Total');
          filters.breakDownList.conditions.values = [
            ...breakdownList,
            ...(isTotalInclude ? ['Total'] : []),
          ];
        }
        //
        dispatch(
          regionalMapActions.getRegionalData({
            organizationId,
            columns: [
              {
                table: 'measures',
                columns: regionalMapLevel,
              },
            ],
            filters,
          })
        );
        dispatch(
          regionalMapActions.getRegionalFullData({
            organizationId,
            columns: REGIONAL_MAP_FULL_DATA_COLUMN,
            filters: ensureRegionalMapFullDataFilters(filters),
          })
        );
        setExportData({
          columns: REGIONAL_MAP_FULL_DATA_COLUMN,
          filters,
          title: 'Regional Map',
        });
      } catch (error) {
        if (isRequestCanceled(error)) return;
        throw error;
      }
    })();
  }, 500);
  // To fetch data when any of the dependencies change
  useEffect(() => {
    if (
      !visualFilters?.length ||
      !hotels ||
      !targetDate?.startDate ||
      !targetDate?.endDate ||
      !bookingDate ||
      isDetailed === undefined ||
      !chartBreakdown ||
      regionalMapLevel === undefined ||
      !regionalBreakdownTabularView ||
      focusOn === undefined ||
      !regionTabularIsDisplay
    ) {
      return;
    }
    fetchData();
  }, [
    bookingDate,
    currencyType,
    hotels,
    targetDate,
    chartBreakdown,
    pms,
    focusOn,
    visualFilters,
    isDetailed,
    organizationId,
    regionalMapLevel,
    regionalMapSelection,
    regionalBreakdownTabularView,
    regionTabularIsDisplay,
  ]);
  // To reset selection and level when any of the dependencies change
  useEffect(() => {
    dispatch(
      regionalMapActions.setRegionalMapSelection({
        continent: null,
        country: null,
        state: null,
        city: null,
      })
    );
    dispatch(regionalMapActions.setRegionalMapLevel('continent'));
    tableRef.current?.setExpandedRowIds(new Set());
  }, [
    bookingDate,
    currencyType,
    hotels,
    targetDate,
    chartBreakdown,
    pms,
    focusOn,
    visualFilters,
    isDetailed,
    organizationId,
  ]);
  // To expand all rows when drill down is turned on
  useEffect(() => {
    if (!isDrillDown || !regionalMapData?.length || !regionalMapData?.[0]?.Continent) {
      return;
    }
    // Generate expandable row ids based on the data structure and levels
    const getExpandableRowIds = (rows) => {
      const ids = [];

      const traverse = (items, level = 0) => {
        items.forEach((item) => {
          if (item.children?.length) {
            ids.push(`${item?.Continent}_${level}_${level}_${level}`);
            traverse(item.children, level + 1);
          }
        });
      };
      traverse(rows);
      return ids;
    };
    const expandableRowIds = getExpandableRowIds(regionalMapData);
    const prvExpandableRowIds = tableRef.current?.expandedRowIds;
    const isSame =
      prvExpandableRowIds?.size === expandableRowIds?.length &&
      expandableRowIds?.every((item) => prvExpandableRowIds?.has(item));
    if (isSame) {
      return;
    }
    if (JSON.stringify(expandableRowIds) === JSON.stringify(prvExpandableRowIds)) {
      return;
    }
    tableRef.current?.setExpandedRowIds((prev) => new Set([...prev, ...expandableRowIds]));
  }, [regionalMapData]);
  // To build table schema based on the data and breakdown
  const tableSchema = useMemo(
    () =>
      buildColumnsFromData(
        getBreakdownValue(visualFilters, chartBreakdown, isDetailed),
        regionalMapData
      ),
    [regionalMapData, isDetailed]
  );
  // To build export table schema based on the data and breakdown
  const exportTableSchema = useMemo(
    () =>
      buildExportColumnsFromData(
        getBreakdownValue(visualFilters, chartBreakdown, isDetailed),
        regionalMapFullData
      ),
    [regionalMapFullData]
  );
  const headerFilterData = useMemo(
    () =>
      buildHeaderFilterData({
        bookingDate,
        targetStartDate: targetDate?.startDate,
        targetEndDate: targetDate?.endDate,
        currencyType,
        hotels,
        pms,
        focusOn,
        visualFilters,
        chartBreakdown,
        regionalMapLevel,
        regionalMapSelection,
        isDetailed,
      }),
    [
      bookingDate,
      currencyType,
      hotels,
      targetDate,
      chartBreakdown,
      pms,
      focusOn,
      visualFilters?.length,
      isDetailed,
      organizationId,
      regionalMapLevel,
      regionalMapSelection,
      regionalBreakdownTabularView,
    ]
  );
  //
  const handleRowClick = (row, currentLevel, parentData) => {
    const { value: selectedValue, key } = getLevel(row);
    let level = regionalMapLevel;
    if (currentLevel !== null && currentLevel !== undefined) {
      if (key === 'continent') {
        switch (currentLevel) {
          case 0:
            level = 'continent';
            break;
          case 1:
            level = 'country';
            break;
          case 2:
            level = 'state';
            break;
          case 3:
            level = 'city';
            break;
          default:
            level = regionalMapLevel;
        }
      } else if (key === 'country') {
        switch (currentLevel) {
          case 0:
            level = 'country';
            break;
          case 1:
            level = 'state';
            break;
          case 2:
            level = 'city';
            break;
          default:
            level = regionalMapLevel;
        }
      } else if (key === 'state') {
        switch (currentLevel) {
          case 0:
            level = 'state';
            break;
          case 1:
            level = 'city';
            break;
          default:
            level = regionalMapLevel;
        }
      }
    }
    dispatch(regionalMapActions.setRegionalMapLevel(getHierarchyNeighbors(level).next));
    //
    if (isDrillDown) {
      dispatch(
        regionalMapActions.setRegionalMapSelection({
          [level]: selectedValue,
        })
      );
    } else {
      const selection = getRelativeLevels(parentData, row, level);
      dispatch(regionalMapActions.setRegionalMapSelection(selection));
    }
  };
  //
  const handleDrillUp = () => {
    const maxLevel = getRegionalMapDataLastLevel(regionalMapData);
    if (tableRef.current?.expandedRowIds?.size) {
      const updatedData = removeMaxLevel(regionalMapData);
      dispatch(regionalMapActions.setRegionalData(updatedData));
      const expandedRowIds = tableRef.current?.expandedRowIds;
      if (maxLevel === 'city') {
        const updatedSet = removeSetValueBySuffix(expandedRowIds, '_2');
        tableRef.current.setExpandedRowIds(updatedSet);
      } else if (maxLevel === 'state') {
        const updatedSet = removeSetValueBySuffix(expandedRowIds, '_1');
        tableRef.current.setExpandedRowIds(updatedSet);
      } else if (maxLevel === 'country') {
        const updatedSet = removeSetValueBySuffix(expandedRowIds, '_0');
        tableRef.current.setExpandedRowIds(updatedSet);
      }
    }
    let selection = {};
    if (maxLevel === 'city') {
      selection = {
        city: null,
        state: null,
      };
    } else if (maxLevel === 'state') {
      selection = {
        state: null,
        city: null,
        country: null,
      };
    } else if (maxLevel === 'country') {
      selection = {
        continent: null,
        country: null,
        state: null,
        city: null,
      };
    }
    dispatch(regionalMapActions.setRegionalMapSelection(selection));
    dispatch(regionalMapActions.setRegionalMapLevel(getHierarchyNeighbors(maxLevel).previous));
  };
  //
  return (
    active && (
      <Grid
        container
        direction="row"
        className={styles.mainGrid}
        sx={{
          display: value === TAB_VALUE.REGION_MAP ? '' : 'none',
        }}
      >
        <Grid
          item
          xs={2}
          cc={2.5}
          md={2}
          sx={{
            ml: {
              xs: 0,
              cc: 0.25,
              md: 0,
            },
          }}
        >
          <RegionalMapFilter
            reportType={reportType}
            tabValue={value}
            latestDate={latestDate}
            hotelTimezone={hotelTimezone}
            toggleDisabled={toggleDisabled}
            isLoaded={isRegionLoaded}
          />
        </Grid>
        {IsFocusOnSet && (
          <Grid item xs={10} cc={9.25} md={10}>
            <Grid item container justifyContent="flex-end" xs={12}>
              <CustomToggleSwitch
                onChange={(event) =>
                  dispatch(regionalMapActions.setRegionalBreakdownTabularView(event.target.checked))
                }
                label="Tabular View"
                checked={regionalBreakdownTabularView}
                paddingBottom={1}
                className="tabular-view-toggle"
              />
            </Grid>
            {!isEmpty(filterList) && (
              <VisualGrid
                isDisplay={regionIsDisplay}
                visualName={getVisualName(0)}
                visual={getWidget(
                  0,
                  regionStyles.regionMapBreakdown,
                  setRegionalMapReport,
                  setIsRegionalMapVisualRendered,
                  regionalMapActions.setRegionMapIsDisplay,
                  setIsRegionLoaded
                )}
                onClick={() => {
                  dispatch(regionalMapActions.setRegionMapIsDisplay(!regionIsDisplay));
                }}
                interpreterFragment={
                  <AssistantIcon
                    visualName={getVisualName(0)}
                    report={regionalMapReport}
                    isProcessing={isV1Processing}
                    setIsProcessing={setIsV1Processing}
                    disabled={
                      !isRegionalMapVisualRendered || isV1Processing || !regionIsDisplay || true
                    }
                    exportType={exportType}
                    setExportType={setExportType}
                  />
                }
              />
            )}
            {/* Cerulean tabular view for testing. don't push into QA or Production */}
            {regionalBreakdownTabularView && (
              <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="flex-start"
                mt={2}
                mb={120}
              >
                <Grid className={styles.paceVisualGrid}>
                  <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ minHeight: 40, px: 2 }}
                  >
                    <Grid item>
                      <Typography>Regional Breakdown (Tabular)</Typography>
                    </Grid>

                    <Grid item>
                      <Grid container alignItems="center">
                        <>
                          <Typography>Export Data</Typography>
                          <IconButton
                            size="small"
                            onClick={() => setIsExportProcessing(true)}
                            disabled={
                              !regionTabularIsDisplay ||
                              isRegionalMapDataLoading ||
                              isRegionalMapFullDataLoading
                            }
                            sx={EXPORT_PDF_BUTTON_STYLE}
                          >
                            <GetAppIcon />
                          </IconButton>
                        </>
                        {ENVIRONMENT.IS_VISUAL_INTERPRETER_ENABLED?.toLowerCase() === 'true' && (
                          <Grid item mt={1} mr={4}>
                            <AssistantIcon
                              exportData={exportData}
                              visualName="Regional Breakdown Tabular"
                              isProcessing={isExporting}
                              setIsProcessing={setIsExporting}
                              disabled={
                                !regionTabularIsDisplay ||
                                isExportProcessing ||
                                isRegionalMapFullDataLoading ||
                                isExporting ||
                                isRegionalMapDataLoading
                              }
                              isCerulean
                              getExcelExportData={exportTableRef?.current?.getExcelData}
                              exportType={exportType}
                              setExportType={setExportType}
                            />
                          </Grid>
                        )}

                        <CollapseIcon
                          isDisplay={regionTabularIsDisplay}
                          onClick={() =>
                            dispatch(
                              regionalMapActions.setRegionMapTabularIsDisplay(
                                !regionTabularIsDisplay
                              )
                            )
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {regionalMapFullData ? (
                    <div style={{ display: 'none' }}>
                      <MuiTabular
                        ref={exportTableRef}
                        columns={exportTableSchema}
                        isProcessing={isExportProcessing}
                        isLoadingData={isRegionalMapFullDataLoading}
                        setIsProcessing={setIsExportProcessing}
                        headerComponent={null}
                        data={regionalMapFullData}
                        headerFiltersInfo={headerFilterData}
                        fileName="RegionalMap"
                        isAggregate={false}
                      />
                    </div>
                  ) : null}
                  {regionTabularIsDisplay && (
                    <Grid>
                      {!regionalMapData ? (
                        <Grid container justifyContent="center" alignItems="center" py={5}>
                          <Typography fontWeight={500}>Loading Data...</Typography>
                        </Grid>
                      ) : (
                        <Box sx={TABLE_CONTAINER_STYLE}>
                          {/* Action Buttons */}
                          <Box sx={ACTION_BUTTON_STYLE}>
                            <Tooltip title="Drill Up">
                              <span>
                                <IconButton
                                  aria-label="Drill Up"
                                  disabled={
                                    (!hasAnyLevelSelected(regionalMapSelection) &&
                                      regionalMapLevel === 'continent') ||
                                    (isDrillDown && regionalMapLevel === 'continent') ||
                                    isRegionalMapDataLoading
                                  }
                                  onClick={handleDrillUp}
                                >
                                  <NorthIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Click to turn on Drill down">
                              <span>
                                <IconButton
                                  aria-label="Click to turn on Drill down"
                                  disabled={isRegionalMapDataLoading || !regionalMapData?.length}
                                  onClick={() =>
                                    dispatch(regionalMapActions.setIsDrillDown(!isDrillDown))
                                  }
                                >
                                  {isDrillDown ? (
                                    <Box sx={DRILL_DOWN_BUTTON_STYLE}>
                                      <SouthIcon sx={{ color: 'white', fontSize: 16 }} />
                                    </Box>
                                  ) : (
                                    <SouthIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Go to the next level in hierarchy">
                              <span>
                                <IconButton
                                  aria-label="Go to the next level in hierarchy"
                                  disabled={
                                    isRegionalMapDataLoading ||
                                    hasAnyLevelSelected(regionalMapSelection) ||
                                    regionalMapLevel === 'city' ||
                                    !regionalMapData?.length
                                  }
                                  onClick={() => {
                                    dispatch(
                                      regionalMapActions.setRegionalMapLevel(
                                        getHierarchyNeighbors(regionalMapLevel).next
                                      )
                                    );
                                  }}
                                >
                                  <SouthIcon fontSize="small" />
                                  <SouthIcon fontSize="small" sx={{ ml: -1 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                          {/* Table */}
                          <Box
                            sx={{
                              overflowX: 'auto',
                              width: '100%',
                            }}
                          >
                            <MuiTabular
                              ref={tableRef}
                              columns={tableSchema}
                              isProcessing={false}
                              isLoadingData={isRegionalMapDataLoading}
                              setIsProcessing={() => {}}
                              isAggregate
                              headerComponent={null}
                              data={regionalMapData}
                              removeEmptyColumns
                              headerFiltersInfo={{}}
                              onAllRowsCollapse={() => {
                                dispatch(regionalMapActions.setRegionalMapLevel('continent'));
                                dispatch(
                                  regionalMapActions.setRegionalMapSelection({
                                    continent: null,
                                    country: null,
                                    state: null,
                                    city: null,
                                  })
                                );
                              }}
                              customBackgroundStyle={{
                                maxWidth: '100%',
                              }}
                              customRowStyle={{ borderColor: '#e0e0de' }}
                              customAggregationRowStyle={AGGREGATE_ROW_STYLE}
                              customTableStyle={{
                                height: 200,
                              }}
                              fileName="RegionalMap"
                              expandableKeys={
                                regionalMapSelection?.continent || regionalMapLevel === 'continent'
                                  ? ['Continent', 'Country', 'State']
                                  : null
                              }
                              isDrillDown={isDrillDown}
                              rowOnClick={handleRowClick}
                            />
                          </Box>
                        </Box>
                      )}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    )
  );
};
//
export default RegionalMap;
